import React, { useCallback, useEffect, useState } from "react";

/* ===================== STATS & GRAPH COMPONENTS ===================== */
import StatsBox from "../../../components/AdminComponents/StatsBox/StatsBox";
import APITrendChart from "../../../components/AdminComponents/APITrendChart/APITrendChart";

/* ===================== COMMON COMPONENTS ===================== */
import Input from "../../../components/Input/Input";
import APITable from "../../../components/Tables/APITable";
import Pagination from "../../../components/Pagination/Pagination";
import DateRangePicker from "../../../components/Calender/DateRangePicker";
import Button from "../../../components/Button/Button";
import Modal from "../../../components/Modal/Modal";
import {
  StatsSkeleton,
  TableSkeleton,
  TrendSkeleton,
} from "../../../components/Skeleton/StatsSkeleton";

/* ===================== ICONS ===================== */
import {
  Zap,
  TrendingUp,
  Clock,
  AlertTriangle,
  Download,
  Settings2,
} from "lucide-react";

/* ===================== SERVICES ===================== */
import { apiGet } from "../../../apiService";
import { can } from "../../../utils/permissions";

/* ===================== STYLES ===================== */
import styles from "./AdminApiUsage.module.scss";

const AdminApiUsage = () => {
  /* ===================== TAB STATE ===================== */
  const [activeTab, setActiveTab] = useState("usage");

  /* ===================== DASHBOARD DATA ===================== */
  const [statsData, setStatsData] = useState([]);
  const [trendData, setTrendData] = useState([]);

  /* ===================== TABLE DATA ===================== */
  const [rows, setRows] = useState([]);
  const [usageLoading, setUsageLoading] = useState(false);
  const [logsLoading, setLogsLoading] = useState(false);
  const [dashloading, setDashloading] = useState(false);
  const [trendloading, setTrendloading] = useState(false);

  const [showErrorModal, setShowErrorModal] = useState(false);
  const [errorDetails, setErrorDetails] = useState(null);

  /* ===================== PAGINATION ===================== */
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [pagination, setPagination] = useState({
    total: 0,
    totalPages: 1,
  });

  /* ===================== SEARCH & FILTER ===================== */
  const [usageSearch, setUsageSearch] = useState("");
  const [logsSearch, setLogsSearch] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [errorCode] = useState("");
  const [datePickerKey, setDatePickerKey] = useState(0);
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const resetDateFilter = () => {
    setFromDate("");
    setToDate("");
    setPage(1);
    setDatePickerKey((prev) => prev + 1);
  };

  const handleErrorClick = (row) => {
    setShowErrorModal(true);
    if (row.status === "Success") {
      setErrorDetails("API Executed Successfully");
    } else {
      setErrorDetails(row.error_response);
    }
  };

  const fetchDashboardData = async () => {
    setDashloading(true);
    setTrendloading(true);
    try {
      const res = await apiGet("catalog", "/api-usage-dashboard");

      if (res?.success) {
        const current = res.currentData?.[0] || {};
        const improvement = res.improvementData || {};

        setStatsData([
          {
            title: "Total API Calls",
            value: current.total_api_call ?? 0,
            type: "blue",
            icon: <Zap size={24} />,
            subText: improvement.api_call_improvement
              ? `${improvement.api_call_improvement}% vs last period`
              : "---",
          },
          {
            title: "Success Rate",
            value: `${current.success_rate ?? 0}%`,
            type: "green",
            icon: <TrendingUp size={24} />,
            subText: improvement.success_rate_improvement
              ? `${improvement.success_rate_improvement}% improvement`
              : "---",
          },
          {
            title: "Average Latency",
            value: `${current.average_latency_ms ?? 0} ms`,
            type: "orange",
            icon: <Clock size={24} />,
            subText: improvement.average_latency_ms_improvement
              ? `${improvement.average_latency_ms_improvement} ms change`
              : "---",
          },
          {
            title: "Error Rate",
            value: `${current.error_rate ?? 0}%`,
            type: "red",
            icon: <AlertTriangle size={24} />,
            subText: improvement.error_rate_improvement
              ? `${improvement.error_rate_improvement}% change`
              : "---",
          },
        ]);

        const labels = res.APICallsTrend?.labels || [];
        const datasets = res.APICallsTrend?.datasets || [];
        const success = datasets.find((d) => d.label === "Success")?.data || [];
        const failed = datasets.find((d) => d.label === "Failed")?.data || [];

        setTrendData(
          labels.map((label, i) => ({
            day: label,
            success: success[i] || 0,
            failed: failed[i] || 0,
          })),
        );
      }
    } catch (err) {
      console.error("Dashboard API error", err);
    } finally {
      setDashloading(false);
      setTrendloading(false);
    }
  };

  const fetchTableData = useCallback(async () => {
    if (activeTab === "usage") setUsageLoading(true);
    else setLogsLoading(true);

    try {
      const query = new URLSearchParams({
        page,
        limit,
        ...(fromDate && toDate && { from_date: fromDate, to_date: toDate }),
        ...(appliedSearch && { search: appliedSearch }),
      }).toString();

      const endpoint =
        activeTab === "usage" ? `/api-usage?${query}` : `/api-logs?${query}`;

      const res = await apiGet("catalog", endpoint);

      if (res?.success) {
        setRows(res.data || []);
        setPagination({
          total: res.pagination.total,
          totalPages: res.pagination.totalPages,
        });
      }
    } catch (err) {
      console.error("Table API error", err);
    } finally {
      if (activeTab === "usage") setUsageLoading(false);
      else setLogsLoading(false);
    }
  }, [activeTab, page, limit, fromDate, toDate, appliedSearch]);

  /* ===================== EFFECTS ===================== */
  useEffect(() => {
    fetchDashboardData();
  }, []);

  useEffect(() => {
    fetchTableData();
  }, [fetchTableData]);

  useEffect(() => {
    setPage(1);
  }, [activeTab, errorCode, fromDate, toDate]);

  /* ===================== TABLE COLUMNS ===================== */
  const usageColumns = [
    { label: "Company name", key: "company_name" },
    { label: "Category", key: "category" },
    { label: "API Name", key: "api_name" },
    { label: "Total Hits", key: "total_hits" },
    { label: "Error Rate (%)", key: "error_rate" },
    { label: "Avg Response (sec)", key: "avg_response_time_sec" },
  ];

  const logsColumns = [
    { label: "Company name", key: "company_name" },
    { label: "Date Time", key: "created_at" },
    { label: "Category", key: "category" },
    { label: "API Name", key: "api_name" },
    { label: "Code", key: "code" },
    { label: "Key Name", key: "key_name" },
    { label: "Server Type", key: "server_type" },
    { label: "Transaction No", key: "transaction_no" },
    { label: "Status", key: "status" },
    { label: "Error Code", key: "error_code" },
    { label: "Credit Consumption", key: "credit_points_consumed" },
    { label: "Response Time (ms)", key: "response_time_ms" },
  ];

  /* ===================== CSV ===================== */
  const handleDownload = () => {
    const cols = activeTab === "usage" ? usageColumns : logsColumns;
    const header = cols.map((c) => c.label).join(",") + "\n";
    const body = rows
      .map((r) => cols.map((c) => r[c.key] ?? "").join(","))
      .join("\n");

    const blob = new Blob([header + body], {
      type: "text/csv;charset=utf-8;",
    });

    const link = document.createElement("a");
    link.href = URL.createObjectURL(blob);
    link.download = activeTab === "usage" ? "api_usage.csv" : "api_logs.csv";
    link.click();
  };

  return (
    <div className={styles["admin-api-usage"]}>
      <div className={styles["admin-api-usage__header"]}>
        <div className={styles["admin-api-usage__icon-wrapper"]}>
          <Settings2 size={24} strokeWidth={2.5} />
        </div>

        <div className={styles["admin-api-usage__title-group"]}>
          <h2 className={styles["admin-api-usage__title"]}>API Usage</h2>
          <p className={styles["admin-api-usage__subtitle"]}>
            Monitor API calls, token consumption, and system integration
            metrics.
          </p>
        </div>
      </div>

      <div className={styles["admin-api-usage__stats-grid"]}>
        {dashloading
          ? [...Array(4)].map((_, i) => <StatsSkeleton key={i} />)
          : statsData.map((item, i) => <StatsBox key={i} {...item} />)}
      </div>

      {trendloading ? (
        <TrendSkeleton columns={2} rows={1} />
      ) : (
        <APITrendChart data={trendData} />
      )}

      <div className={styles["admin-api-usage__card"]}>
        <div className={styles["admin-api-usage__controls-row"]}>
          <div className={styles["admin-api-usage__tabs"]}>
            <button
              className={`${styles["admin-api-usage__tab"]} ${
                activeTab === "usage"
                  ? styles["admin-api-usage__tab--active"]
                  : ""
              }`}
              onClick={() => {
                setActiveTab("usage");
                resetDateFilter();
                setAppliedSearch("");
                setUsageSearch("");
                setLogsSearch("");
              }}
            >
              API Usage
            </button>

            <button
              className={`${styles["admin-api-usage__tab"]} ${
                activeTab === "logs"
                  ? styles["admin-api-usage__tab--active"]
                  : ""
              }`}
              onClick={() => {
                setActiveTab("logs");
                resetDateFilter();
                setAppliedSearch("");
                setUsageSearch("");
                setLogsSearch("");
              }}
            >
              API Logs
            </button>
          </div>

          <div className={styles["admin-api-usage__search-box"]}>
            <Input
              type="search"
              placeholder="Search API, Error Code"
              value={activeTab === "usage" ? usageSearch : logsSearch}
              onChange={(e) =>
                activeTab === "usage"
                  ? setUsageSearch(e.target.value)
                  : setLogsSearch(e.target.value)
              }
              onKeyDown={(e) => {
                if (e.key === "Enter") {
                  setAppliedSearch(
                    activeTab === "usage" ? usageSearch : logsSearch,
                  );
                  setPage(1);
                }
              }}
            />
            <Button
              text="Search"
              className={styles["admin-api-usage__search-btn"]}
              onClick={() => {
                setAppliedSearch(
                  activeTab === "usage" ? usageSearch : logsSearch,
                );
                setPage(1);
              }}
            />
          </div>
        </div>

        <div className={styles["admin-api-usage__table-actions"]}>
          <div className={styles["admin-api-usage__showing-text"]}>
            Showing {rows.length} of {pagination.total}
          </div>

          <div className={styles["admin-api-usage__filters"]}>
            <DateRangePicker
              key={datePickerKey}
              maxDate={new Date()}
              onChange={(type, value) => {
                if (type === "from") {
                  setFromDate(value);
                  setPage(1);
                }
                if (type === "to") {
                  setToDate(value);
                  setPage(1);
                }
              }}
            />

            {can("api-usage", "export") && (
              <button
                className={styles["admin-api-usage__download-btn"]}
                onClick={handleDownload}
              >
                <Download size={18} />
                Download
              </button>
            )}
          </div>
        </div>

        {activeTab === "usage" && usageLoading ? (
          <TableSkeleton columns={6} rows={4} />
        ) : activeTab === "logs" && logsLoading ? (
          <TableSkeleton columns={6} rows={5} />
        ) : (
          <APITable
            columns={activeTab === "usage" ? usageColumns : logsColumns}
            data={rows}
            renderCell={(row, key) => {
              /* CUSTOM: Date Time */
              if (activeTab === "logs" && key === "created_at") {
                const datetime = row.created_at;
                if (!datetime) return "--";
                const [date, time, meridian] = datetime.split(" ");
                return (
                  <span>
                    {date}{" "}
                    <span className={styles["admin-api-usage__time-color"]}>
                      {time} {meridian}
                    </span>
                  </span>
                );
              }

              /* CUSTOM: Clickable Error Code */
              if (activeTab === "logs" && key === "error_code") {
                return (
                  <span
                    className={styles["admin-api-usage__error-link"]}
                    onClick={() => handleErrorClick(row)}
                  >
                    {row.error_code}
                  </span>
                );
              }

              /* CUSTOM: Pill Status Badge (Rule 12: No Borders) */
              if (activeTab === "logs" && key === "status") {
                const isSuccess = row.status === "Success";
                return (
                  <span
                    className={`${styles["admin-api-usage__status-badge"]} ${
                      isSuccess
                        ? styles["admin-api-usage__status-badge--success"]
                        : styles["admin-api-usage__status-badge--error"]
                    }`}
                  >
                    {row.status}
                  </span>
                );
              }
              return null;
            }}
          />
        )}

        {showErrorModal && (
          <Modal
            title="Response"
            onClose={() => setShowErrorModal(false)}
            className="error_details_modal"
          >
            <pre className={styles["admin-api-usage__json-block"]}>
              {(() => {
                if (!errorDetails) return "--";

                if (typeof errorDetails === "string") {
                  try {
                    const parsed = JSON.parse(errorDetails);
                    return JSON.stringify(parsed, null, 2);
                  } catch {
                    return errorDetails;
                  }
                }

                return JSON.stringify(errorDetails, null, 2);
              })()}
            </pre>
          </Modal>
        )}

        <Pagination
          currentPage={page}
          totalPages={pagination.totalPages}
          rowsPerPage={limit}
          onPageChange={setPage}
          onRowsPerPageChange={(l) => {
            setLimit(l);
            setPage(1);
          }}
        />
      </div>
    </div>
  );
};

export default AdminApiUsage;
